import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, X } from 'lucide-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { getInstitutionRequests, getUserInfo } from '../../utils/remote';
import { useAlertsInbox } from '../../school_panel/api/hooks';
import { roleLabel } from '../Header/DropdownUser';
import LogoMark from '../../images/logo/logo.png';
import { sectionsForRole } from './navConfig';

interface SidebarProps { sidebarOpen: boolean; setSidebarOpen: (arg: boolean) => void }

function initials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return p.length === 0 ? '?' : (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

export default function Sidebar({ sidebarOpen, setSidebarOpen }: SidebarProps) {
  const { role, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [institution, setInstitution] = useState<string | null>(null);
  const [userName, setUserName] = useState('');

  const isPanelUser = role === 'school_admin' || role === 'teacher' || role === 'counselor' || role === 'school' || role === 'admin';
  const inbox = useAlertsInbox({});
  const alerts = useMemo(() => (isPanelUser && inbox.data ? inbox.data.results.length : 0), [isPanelUser, inbox.data]);
  const isSchoolAdmin = role === 'school_admin' || role === 'school' || role === 'admin';
  // Same query key as the Student requests page, so approving there refreshes the badge.
  const requestsQ = useQuery({
    queryKey: ['institutionRequests'],
    queryFn: () => getInstitutionRequests(token),
    enabled: isSchoolAdmin && !!token,
    refetchInterval: 5 * 60 * 1000,
  });
  const requests = useMemo(() => {
    const rows: { status?: string }[] = requestsQ.data?.requests ?? [];
    return rows.filter((r) => r.status === 'pending').length;
  }, [requestsQ.data]);
  const sections = sectionsForRole(role, { alerts, requests });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && sidebarOpen) setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // The token comes from AuthProvider: without "Remember me" it lives in sessionStorage,
  // so reading localStorage here left the name card on "—".
  useEffect(() => {
    if (!token) return;
    getUserInfo(token).then((d) => {
      setInstitution(d.institution_name || null);
      const name = `${d.first_name || d.name || ''} ${d.last_name || ''}`.trim();
      setUserName(name || d.username || '');
    }).catch(() => {});
  }, [role, token]);

  return (
    <>
      {sidebarOpen && (
        <div aria-hidden className="fixed inset-0 z-40 bg-navy/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside
        aria-label="Main navigation"
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-navy px-3.5 pb-5 pt-[22px] text-on-navy-muted transition-transform duration-200 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center gap-3 px-2.5 pb-2.5">
          <Link to={sections[0]?.links[0]?.to ?? '/'} className="flex min-w-0 flex-1 items-center gap-3 text-on-navy-muted no-underline hover:text-on-navy-muted">
            <img src={LogoMark} alt="" className="h-[38px] w-[38px] flex-none rounded-[11px]" />
            <span className="min-w-0 flex-1">
              <span className="block font-display text-[17px] font-bold tracking-[-0.01em] text-white">YADA School</span>
              {institution && <span className="block truncate text-xs">{institution}</span>}
            </span>
          </Link>
          <button type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)}
            className="grid h-9 w-9 place-items-center rounded-[10px] p-0 text-on-navy hover:bg-navy-2 lg:hidden">
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        <nav className="no-scrollbar flex flex-1 flex-col gap-0.5 overflow-y-auto">
          {sections.map((section) => (
            <div key={section.heading} className="flex flex-col gap-0.5">
              <div className="mx-3 mb-2 mt-[22px] font-mono text-[10.5px] font-semibold uppercase leading-none tracking-[0.1em] text-on-navy-faint">
                {section.heading}
              </div>
              {section.links.map((l, _i, links) => {
                const Icon = l.icon;
                const exact = links.some((o) => o.to !== l.to && o.to.startsWith(l.to + '/'));
                return (
                  <NavLink key={l.to} to={l.to} end={exact}
                    className={({ isActive }) =>
                      'flex min-h-[40px] items-center gap-3 rounded-[10px] px-3 text-sm no-underline transition-colors ' +
                      (isActive ? 'bg-accent font-semibold text-navy' : 'font-medium text-on-navy hover:bg-navy-2 hover:text-white')}>
                    {({ isActive }) => (
                      <>
                        <Icon size={18} strokeWidth={1.8} aria-hidden />
                        <span className="flex-1">{l.label}</span>
                        {l.badge && (
                          <span className={`ml-auto rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold ${
                            isActive ? 'bg-navy text-accent'
                              : l.badge.tone === 'alert' ? 'bg-alert-pill text-navy' : 'bg-navy-3 text-mist-fixed'}`}>
                            {l.badge.value}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="mt-7 flex items-center gap-2.5 rounded-[14px] bg-navy-deep p-3">
          <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-mist text-[13px] font-semibold text-ink">
            {initials(userName || 'User')}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-white">{userName || '—'}</span>
            <span className="block text-xs">{roleLabel(role)}</span>
          </span>
          <button type="button" aria-label="Log out" onClick={() => navigate('/logout')}
            className="grid h-9 w-9 place-items-center rounded-[10px] p-0 text-on-navy hover:bg-navy-3 hover:text-white">
            <LogOut size={18} strokeWidth={1.8} />
          </button>
        </div>
      </aside>
    </>
  );
}
